import { Injectable, ComponentRef, Type, Inject, ViewContainerRef } from '@angular/core';
import { Obj, Func, Str } from '../../utils';
import { List } from '../../collections';
import { ComponentResolverService } from '.';
import { 
  IComponentResolverService,
  IModalService 
} from '../interfaces';
import { OverlayComponent } from '../overlay';
import { 
  ModalActionType,
  ModalButtonAlignment,
  ModalButtonSeverity 
} from '../enums';
import { 
  ModalButton,
  ModalButtonList 
} from '../models';
export class ModalService implements IModalService {
public buttons: ModalButtonList;
public visible: boolean;
public wrapper: ViewContainerRef;
public overlayIsBlocking: boolean;
public modalComponent: ComponentRef<any>;
public closeCallbacks: List<Function>;
public completeCallbacks: List<Function>;
public additionalClasses: string;
/**
 * @param {?} _componentResolverService
 */
constructor(
private _componentResolverService: IComponentResolverService
  ) {
    // Nullify the modal component and wrapper to ensure a clean start
    this.wrapper = null;
    this.modalComponent = null;

    // hide modal by default
    this.visible = false;

    // List of additional classes to add to the container element
    this.additionalClasses = Str.EMPTY;

    // Default - determines whether clicking the overlay has any effect.
    // blocking = true indicates clicking the overlay does *not* close the modal.
    this.overlayIsBlocking = true;

    // Instantiate all default modal buttons
    this.buttons = new ModalButtonList();

    // Wire up the OK button to invoke the primary action
    let okButton = new ModalButton(
      'OK', 
      ModalActionType.OK,
      ModalButtonAlignment.Right,
      ModalButtonSeverity.Primary,
      () => {
        console.log('ok button clicked');
      },
      false
    );

    // Wire up the close button to invoke the close action
    let closeButton = new ModalButton(
      'Close', 
      ModalActionType.Close,
      ModalButtonAlignment.Right,
      ModalButtonSeverity.Normal,
      () => {
        console.log('close button clicked');
        this.close();
      },
      false
    );

    // Wire up the cancel button to have default close functionality
    let cancelButton = new ModalButton(
      'Cancel', 
      ModalActionType.Cancel, 
      ModalButtonAlignment.Right,
      ModalButtonSeverity.Warn,
      () => {
        console.log('cancel button clicked');
        this.close();
      },
      false
    );

    // Wire up the execute button for default execute functionality
    let executeButton = new ModalButton(
      'Execute', 
      ModalActionType.Execute,
      ModalButtonAlignment.Left,
      ModalButtonSeverity.Success,
      () => {
        console.log('Execute button clicked');
      },
      false
    );

    // Add all the default buttons
    this.buttons.add(okButton, cancelButton, closeButton, executeButton);

    // Callback funcion arrays
    this.closeCallbacks = new List<Function>();
    this.completeCallbacks = new List<Function>();
  }
/**
 * Returns the current modal component, which is the component that is rendered
 * within the modal wrapper.
 * @return {?}
 */
public getModalComponent(): ComponentRef<any> {
    return this.modalComponent;
  }
/**
 * Returns whether there is currently a modal component specified
 * @return {?}
 */
public hasModalComponent(): boolean {
    return Obj.isNotNullOrUndefined(this.modalComponent);
  }
/**
 * Sets whether the overlay is blocking (blocking = clicking overlay does *not* close
 * the modal).
 *                                  otherwise, false.
 * @param {?} overlayBlocking
 * @return {?}
 */
public setOverlayBlocking(overlayBlocking: boolean): void {
    this.overlayIsBlocking = overlayBlocking === false ? false : this.overlayIsBlocking;
  }
/**
 * Returns whether the overlay is blocking.
 * @return {?}
 */
public isOverlayBlocking(): boolean {
    return this.overlayIsBlocking;
  }
/**
 * Click handler for the overlay click event
 * @return {?}
 */
public overlayClick(): void {
    if(!this.isOverlayBlocking()) {
      this.close();
    }
  }
/**
 * Returns whether or not the modal is visible
 * @return {?}
 */
public isVisible(): boolean {
    return this.visible;
  }
/**
 * Sets whether the modal is visible or not
 * @param {?} isVisible
 * @return {?}
 */
public setVisibility(isVisible: boolean): void {
    this.visible = isVisible === true;
  }
/**
 * Adds the given button type to the button list
 * @param {?} text
 * @param {?} type
 * @return {?}
 */
public addButton(text: string, type: ModalActionType): void {
    this.buttons.add(new ModalButton(text, type));
  }
/**
 * Adds the given function as a click event handler to the button with the give type
 * @param {?} type
 * @param {?} action
 * @return {?}
 */
public onButtonClick(type: ModalActionType, action: Function): void {
    let /** @type {?} */ button = this.buttons.getButtonByType(type);
    if(Obj.isNullOrUndefined(button)) {
      throw Error(`Button of type ${type} does not exist; 
        you may need to create the button first using .addButton(...)`);
    }
    button.setAction(action);
  }
/**
 * Opens a new modal window, optionally inserting it into a target container, with a blocking 
 * or non-blocking overlay
 * 
 *                                 to use as the contents of the modal.
 *                                 is blocking (it blocks click events to the document 
 *                                 beneath the overlay), or non-blocking (the user can close
 *                                 the modal by clicking on the overlay).
 * @template T
 * @param {?} component
 * @param {?=} blocking
 * @param {?=} additionalClasses
 * @return {?}
 */
public open<T>(component: Type<T>, blocking?: boolean, additionalClasses?: string[]): void {

    // Ensure the wrapper is valid before attempting to create the modal contents
    this._checkWrapper(this.wrapper);

    // Prevent the modal from opening twice if it's already open
    if(this.visible || Obj.isNotNullOrUndefined(this.modalComponent)) {
      return;
    }
     
    // Show the modal
    this.setVisibility(true);

    // Set additional classes
    this._setAdditionalClasses(additionalClasses);

    // Set overlay blocking; null or undefined will resolve to false.
    this.setOverlayBlocking(blocking);
    
    // Dynamically create the component which contains an element
    // reference to the template that will be rendered in the modal
    this.modalComponent = this._componentResolverService.createComponent<T>(
      component,
      this.wrapper
    );

    // prevent body from scrolling while modal is open
    document.body.style.overflow = 'hidden';
  }
/**
 * @param {?} additionalClasses
 * @return {?}
 */
private _setAdditionalClasses(additionalClasses: string[]): void {
    if(!additionalClasses) {
      this.additionalClasses = Str.EMPTY;
      return;
    }
    additionalClasses.forEach((additionalClass: string, i: number) => {
      this.additionalClasses += additionalClass + (i < additionalClasses.length - 1 ? ' ': Str.EMPTY);
    });
  }
/**
 * Closes the modal, destroying the modal child (if exists) and overlay components
 * @return {?}
 */
public close(): void {
    if(this.hasModalComponent()) {
      this.modalComponent.destroy();
      this.modalComponent = null;
    }
    this.setVisibility(false);
    this._setAdditionalClasses(null);
    console.log('close modal');
    this.closeCallbacks.removeEach(onClose => onClose());
    document.body.style.removeProperty('overflow');
  }
/**
 * Registers a callback to occur when the modal closes
 * @param {?} callback
 * @return {?}
 */
public onClose(callback: Function): void {
    // Do runtime check
    if(Func.isAFunction(callback)) {
      this.closeCallbacks.add(callback);
    }
  }
/**
 * Triggers when the modal primary action is completed
 * @return {?}
 */
public complete(): void {
    this.close();
    this.completeCallbacks.removeEach(onComplete => onComplete());
  }
/**
 * Registers callback functions to execute when the modal complete method is invoked
 * @param {?} callback
 * @return {?}
 */
public onComplete(callback: Function): void {
    // Do runtime check
    if(Func.isAFunction(callback)) {
      this.completeCallbacks.add(callback);
    }
  }
/**
 * Sets the target warpper container in which to inject the modal.
 * @param {?} wrapper
 * @return {?}
 */
public setWrapper(wrapper: ViewContainerRef): void {
    this._checkWrapper(wrapper);
    this.wrapper = wrapper;
  }
/**
 * Checks that there is a valid wrapper, which is a ViewContainerRef,
 * otherwise throws an error
 * @param {?} wrapper
 * @return {?}
 */
private _checkWrapper(wrapper: ViewContainerRef): void { 
    if(Obj.isNullOrUndefined(wrapper)) {
      throw Error('No target container has been specified.');
    }  
  }
static decorators: DecoratorInvocation[] = [
{ type: Injectable },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: undefined, decorators: [{ type: Inject, args: [ComponentResolverService, ] }, ]},
];
}

function ModalService_tsickle_Closure_declarations() {
/** @type {?} */
ModalService.decorators;
/**
 * @nocollapse
 * @type {?}
 */
ModalService.ctorParameters;
/** @type {?} */
ModalService.prototype.buttons;
/** @type {?} */
ModalService.prototype.visible;
/** @type {?} */
ModalService.prototype.wrapper;
/** @type {?} */
ModalService.prototype.overlayIsBlocking;
/** @type {?} */
ModalService.prototype.modalComponent;
/** @type {?} */
ModalService.prototype.closeCallbacks;
/** @type {?} */
ModalService.prototype.completeCallbacks;
/** @type {?} */
ModalService.prototype.additionalClasses;
/** @type {?} */
ModalService.prototype._componentResolverService;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
